/**
 * D-EZ-111 — THE STORY BIBLE: what the booth knows going into today, for the model.
 *
 * When a model is available (`aiAccess()`: the owner, or a player's own key)
 * each narrate request carries this compact, per-race brief alongside the
 * beat's facts: today's storylines, the analyst's pick and why, the rivalries
 * in this field with the moment each one turns on, and the league history that
 * matters to the drivers on track. With it the model can call back, keep the
 * analyst's opinions consistent, and sound like a booth that has watched this
 * league — without inventing a thing.
 *
 * THE SAME MEMORY AS THE WRITTEN LINES. Every string here is one the written
 * broadcast also draws on, built from history up to the previous race; every
 * name and number in it travels as a Fact too, so the client's gate
 * (`verifyLine`) accepts exactly what the bible says and nothing more.
 *
 * The voices are NOT here: the browser sends cast ids and the Worker owns the
 * briefs (see the header of `cast.ts`). The bible is data, and the Worker tells
 * the model to treat it as data.
 */

import type { Cast } from './cast.ts';
import { castIds, castWords } from './cast.ts';
import type { Fact } from './researcher.ts';
import type { BoothLeague } from './league.ts';
import type { Storyline } from './storylines.ts';
import type { AnalystPick } from './analyst.ts';
import { pairHistory } from './storylines.ts';
import { pastFacts } from './analyst.ts';
import { hottestRivalries } from '../people/rivalry.ts';
import { fact, plural, spokenTrack } from './kit.ts';

export interface StoryBible {
  /** Pool ids — the Worker looks the voices up; nothing descriptive travels. */
  cast: { host: string; analyst: string; pit: string };
  /** Who is who, by name and seat, so the model can address them. */
  booth: string[];
  /** The analyst's own past (fiction about the analyst, fixed in the cast table). */
  analystPast: string;
  /** The analyst's pick before the green, and last week's, as already said on air. */
  pick: string | null;
  storylines: string[];
  rivalries: string[];
  history: string[];
  /** Every fact behind the strings above — the verifier's vocabulary. */
  facts: Fact[];
  /** Words the booth may say about itself (cast names, the analyst's old rival). */
  words: string[];
}

export function buildBible(input: {
  league: BoothLeague | null;
  cast: Cast;
  stories: readonly Storyline[];
  pick: AnalystPick | null;
  pickLine: string | null;
  entered: ReadonlySet<string>;
  nameOf: (id: string) => string;
  shortOf: (id: string) => string;
  venue: string | null;
  key: string;
  seq: number;
}): StoryBible {
  const { league, cast, stories, pick, entered, nameOf, shortOf, venue, key, seq } = input;
  const facts: Fact[] = [];
  const p = cast.past;
  const analystPast = `${cast.analyst.name} drove ${p.seasons} seasons and won ${p.wins} races${p.titles ? ` and ${plural(p.titles, 'title')}` : ''}, ${p.strongWins} of them on ${p.strongKind.toLowerCase()}s; old rival ${p.rival}, ${p.rivalYears} seasons of it. Leans towards ${p.lean}; no patience for ${p.grudge}.`;
  facts.push(...pastFacts(cast));

  for (const s of stories) facts.push(...s.facts);
  if (pick) facts.push(...pick.facts, fact('take', `the analyst picked ${pick.name}`, 0, [pick.driverId]));
  const lp = league?.lastPick;
  if (lp) facts.push(fact('take', `last week the analyst picked ${lp.name} in the ${lp.raceName}; he finished ${lp.place ?? 'out'}`, lp.place ?? 0, [lp.driverId]));

  const rivalries: string[] = [];
  if (league?.people) {
    const now = { seasonNumber: league.seasonNumber, raceNumber: league.raceNumber };
    for (const r of hottestRivalries(league.people.book, 12)) {
      if (!entered.has(r.a) || !entered.has(r.b)) continue;
      const h = pairHistory(r, nameOf, now, venue, key, seq, shortOf);
      if (!h) continue;
      rivalries.push(`${nameOf(r.a)} and ${nameOf(r.b)} (${r.state}): ${h.text}`);
      facts.push(...h.facts, fact('rivalry', `${nameOf(r.a)} and ${nameOf(r.b)} ${r.state}`, 0, [r.a, r.b]));
      if (rivalries.length >= 4) break;
    }
  }

  const history: string[] = [];
  const s = league?.season;
  const add = (text: string, f: Fact) => {
    if (history.length < 8) {
      history.push(text);
      facts.push(f);
    }
  };
  if (league && league.table.length >= 2) {
    const top = league.table.slice(0, 3);
    add(`Standings coming in, in points: ${top.map((t) => `${t.name} ${t.points}`).join(', ')}.`, fact('season', top.map((t) => `${t.rank} ${t.name} ${t.points}`).join(' · '), top[0].points, top.map((t) => t.driverId)));
    for (const t of top) facts.push(fact('season', `${t.name} ${t.points} points`, t.points, [t.driverId]), fact('season', `${t.name} rank ${t.rank}`, t.rank, [t.driverId]));
  }
  if (s) {
    if (s.defendingChampion && entered.has(s.defendingChampion.driverId)) add(`${s.defendingChampion.name} is the defending champion (season ${s.defendingChampion.seasonNumber}).`, fact('season', `${s.defendingChampion.name} champion season ${s.defendingChampion.seasonNumber}`, s.defendingChampion.seasonNumber, [s.defendingChampion.driverId]));
    if (s.defendingRaceWinner && entered.has(s.defendingRaceWinner.driverId)) add(`${s.defendingRaceWinner.name} won this race in season ${s.defendingRaceWinner.seasonNumber}.`, fact('history', `${s.defendingRaceWinner.name} won this race in season ${s.defendingRaceWinner.seasonNumber}`, s.defendingRaceWinner.seasonNumber, [s.defendingRaceWinner.driverId]));
    for (const w of s.trackWinners.filter((x) => entered.has(x.driverId)).slice(0, 2)) add(`${w.name} has ${plural(w.wins, 'win')} at this track.`, fact('history', `${w.name} ${w.wins} wins at this track`, w.wins, [w.driverId]));
    for (const st of s.activeStreaks.filter((x) => entered.has(x.driverId)).slice(0, 2)) add(`${st.name}: ${st.length} straight ${st.kind === 'wins' ? 'wins' : st.kind === 'top5' ? 'top-five finishes' : st.kind === 'top10' ? 'top-ten finishes' : 'finishes'}.`, fact('history', `${st.name} streak ${st.length}`, st.length, [st.driverId]));
    for (const m of s.milestonesInPlay.filter((x) => entered.has(x.driverId) && (x.kind !== 'first-win' || x.n >= 10)).slice(0, 3)) add(m.text, fact('history', m.text, m.n, [m.driverId]));
  }
  const lw = league?.lastRace;
  if (lw?.winnerName && lw.winnerId) add(`Last week: ${lw.winnerName} won the ${lw.raceName} at ${spokenTrack(lw.trackName)}.`, fact('history', `${lw.winnerName} won last week's ${lw.raceName} at ${spokenTrack(lw.trackName)}`, 0, [lw.winnerId]));

  return {
    cast: castIds(cast),
    booth: [`${cast.host.name} (${cast.host.seat})`, `${cast.analyst.name} (${cast.analyst.seat})`, `${cast.pit.name} (${cast.pit.seat})`],
    analystPast,
    pick: input.pickLine,
    storylines: stories.map((x) => x.text),
    rivalries,
    history,
    facts,
    words: castWords(cast),
  };
}
